import { set } from '@ember/object';
import Service, { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency';
import type DeviceStatusModel from 'garaje/models/device-status';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type MetricsService from 'garaje/services/metrics';

export default class DeviceStatusService extends Service {
  @service declare store: StoreService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare metrics: MetricsService;

  @tracked statusByDeviceId = new Map<string, DeviceStatusModel>();
  @tracked fetchError: Error | null = null;
  @tracked lastFetchedLocationId: string | null = null;

  fetchInitialStatuses = restartableTask(async (locationId: string, product?: string) => {
    try {
      const queryParams: Record<string, string | number> = {
        location_id: locationId,
        limit: 100,
        ...(product ? { product } : {}),
      };
      const statuses = await this.store.query('device-status', queryParams);
      const newMap = new Map<string, DeviceStatusModel>();
      for (const status of statuses.slice()) {
        // eslint-disable-next-line ember/use-ember-get-and-set -- Map.set, not Ember set
        newMap.set(status.externalId, status);
      }
      this.statusByDeviceId = newMap;
      this.lastFetchedLocationId = locationId;
      this.fetchError = null;
    } catch (error) {
      this.fetchError = error as Error;
      this.metrics.logMonitorEvent('DEVICES_SERVICE_FETCH_ERROR', {
        location_id: locationId,
        error: (error as Error).message,
      });
    }
  });

  onConnectionStatusChanged(payload: {
    external_id: string;
    product: string;
    connection_status: 'CONNECTED' | 'DISCONNECTED';
    connection_status_updated_at: string;
  }): void {
    // eslint-disable-next-line ember/use-ember-get-and-set -- Map.get, not Ember get
    const existing = this.statusByDeviceId.get(payload.external_id);
    if (existing) {
      set(existing, 'connectionStatus', payload.connection_status);
      set(existing, 'connectionStatusUpdatedAt', new Date(payload.connection_status_updated_at));
    } else {
      const newMap = new Map(this.statusByDeviceId);
      const partial = this.store.push({
        data: {
          id: payload.external_id,
          type: 'device-status',
          attributes: {
            externalId: payload.external_id,
            product: payload.product,
            connectionStatus: payload.connection_status,
            connectionStatusUpdatedAt: payload.connection_status_updated_at,
          },
        },
      }) as DeviceStatusModel;
      // eslint-disable-next-line ember/use-ember-get-and-set -- Map.set, not Ember set
      newMap.set(payload.external_id, partial);
      this.statusByDeviceId = newMap;
    }
  }

  getStatus(externalId: string): DeviceStatusModel | null {
    // eslint-disable-next-line ember/use-ember-get-and-set -- Map.get, not Ember get
    return this.statusByDeviceId.get(externalId) ?? null;
  }

  async ensureStatuses(locationId: string, product?: string): Promise<void> {
    const task = this.fetchInitialStatuses;
    if (task.isRunning) {
      await task.last;
      return;
    }
    if (this.lastFetchedLocationId === locationId) {
      return;
    }
    await task.perform(locationId, product);
  }

  clearStatuses(): void {
    void this.fetchInitialStatuses.cancelAll();
    this.statusByDeviceId = new Map();
    this.lastFetchedLocationId = null;
  }
}

declare module '@ember/service' {
  interface Registry {
    'device-status-service': DeviceStatusService;
  }
}
